// Matryoshka info panel — stack-based drilldown for celestial bodies.
//
// Behaviour:
//   - open(bodyId) shows the body's root panel
//   - clicking a child PUSHES into a deeper panel
//   - clicking a breadcrumb crumb POPS back to that depth
//   - URL hash mirrors the current path so any depth is shareable
//     (#/earth/atmosphere/composition)
//
// Only one panel is visible at a time; transitions slide left/right by
// direction so depth feels physical.

import { getBody, resolvePath } from '../data/bodies/index.js';
import { readout } from '../site/readouts.js';
import { COMPACT_LAYOUT_QUERY } from '../mobiledevice.js';

let rootEl = null;
let breadcrumbEl = null;
let stageEl = null;
let currentPath = [];        // ['earth', 'atmosphere', 'composition']
let isOpenState = false;
let suppressHashSync = false;
let initialized = false;
let panelCleanupTimer = null;
// Live stats: an entry with `readout` (a key of readouts.js READOUTS) takes
// its value from the viewer's clock when it renders and every second while
// the panel is open. The viewer passes its clock in with setClock.
let clockJD = () => Date.now() / 86400000 + 2440587.5;
let liveTimer = null;
function liveValue(e) {
  try { return readout(e.readout, e.body, clockJD()) || e.value || ''; } catch { return e.value || ''; }
}
function refreshLive() {
  if (!stageEl) return;
  stageEl.querySelectorAll('dd[data-readout]').forEach((dd) => {
    dd.textContent = liveValue({ readout: dd.dataset.readout, body: dd.dataset.body, value: dd.textContent });
  });
}
function setClock(fn) {
  clockJD = fn;
  if (!liveTimer) liveTimer = setInterval(() => { if (isOpenState) refreshLive(); }, 1000);
}

export function init() {
  if (initialized) return;
  rootEl = document.getElementById('matryoshka');
  breadcrumbEl = document.getElementById('matryoshka-breadcrumb');
  stageEl = document.getElementById('matryoshka-stage');

  if (!rootEl || !breadcrumbEl || !stageEl) {
    console.error('[matryoshka] missing DOM nodes:', {
      root: !!rootEl, breadcrumb: !!breadcrumbEl, stage: !!stageEl
    });
    return;
  }
  console.info('[matryoshka] init OK, DOM nodes wired');

  const closeBtn = document.getElementById('matryoshka-close');
  if (closeBtn) closeBtn.addEventListener('click', close);

  // Enlarge: a wider panel with bigger type for reading, docked layouts
  // only (the phone sheet is already full width). Kept per browser.
  const expandBtn = document.getElementById('matryoshka-expand');
  if (expandBtn) {
    const setWide = (wide) => {
      rootEl.classList.toggle('is-wide', wide);
      expandBtn.setAttribute('aria-pressed', String(wide));
      const label = wide ? 'Make panel smaller' : 'Enlarge panel';
      expandBtn.setAttribute('aria-label', label);
      expandBtn.title = label;
    };
    let wide = false;
    try { wide = localStorage.getItem('ss.infoWide') === '1'; } catch { /* storage blocked */ }
    setWide(wide);
    expandBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const next = !rootEl.classList.contains('is-wide');
      setWide(next);
      try { localStorage.setItem('ss.infoWide', next ? '1' : '0'); } catch { /* storage blocked */ }
    });
  }

  // Dock: full height in the middle of the right edge, where it opens at
  // first, or half height in the top or bottom right corner, so the rest of
  // the view stays clear. Each press moves it on to the next, round the
  // three; docked layouts only (index.html), kept per browser.
  const dockBtn = document.getElementById('matryoshka-dock');
  if (dockBtn) {
    const DOCKS = ['middle', 'top', 'bottom'];
    const NEXT = {
      middle: 'Move panel to the top right',
      top: 'Move panel to the bottom right',
      bottom: 'Make panel full height',
    };
    let dock = 'middle';
    const setDock = (next) => {
      dock = next;
      rootEl.classList.toggle('dock-top', dock === 'top');
      rootEl.classList.toggle('dock-bottom', dock === 'bottom');
      dockBtn.setAttribute('aria-label', NEXT[dock]);
      dockBtn.title = NEXT[dock];
    };
    try {
      const saved = localStorage.getItem('ss.infoDock');
      if (DOCKS.includes(saved)) dock = saved;
    } catch { /* storage blocked */ }
    setDock(dock);
    dockBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      setDock(DOCKS[(DOCKS.indexOf(dock) + 1) % DOCKS.length]);
      try { localStorage.setItem('ss.infoDock', dock); } catch { /* storage blocked */ }
    });
  }

  // Mobile-only minimise. The panel is a bottom sheet on mobile that
  // obscures the canvas; tapping this chevron collapses it to just the
  // header bar so users can see the planet they opened. Tapping the
  // (minimised) header bar itself re-expands.
  const minBtn = document.getElementById('matryoshka-minimize');
  if (minBtn) {
    minBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      rootEl.classList.toggle('minimized');
    });
  }
  const headerEl = rootEl && rootEl.querySelector('.matryoshka-header');
  if (headerEl) {
    headerEl.addEventListener('click', (e) => {
      // Only tap-to-expand the header when minimised; don't swallow
      // taps on the child buttons.
      if (!rootEl.classList.contains('minimized')) return;
      if (e.target.closest('button, a')) return;
      rootEl.classList.remove('minimized');
    });
  }

  // Body prev/next chevrons in the panel header (mobile only). Delegate
  // to the existing bottom planet-nav buttons so the same navigatePlanet
  // code path handles everything. Lets the user hop between planets
  // without dismissing the info sheet first.
  const bodyPrev = document.getElementById('matryoshka-body-prev');
  const bodyNext = document.getElementById('matryoshka-body-next');
  const dispatch = (targetId) => () => {
    const btn = document.getElementById(targetId);
    if (btn) btn.click();
  };
  if (bodyPrev) bodyPrev.addEventListener('click', (e) => { e.stopPropagation(); dispatch('prev-planet')(); });
  if (bodyNext) bodyNext.addEventListener('click', (e) => { e.stopPropagation(); dispatch('next-planet')(); });

  window.addEventListener('hashchange', applyHash);
  initialized = true;
}

export function open(bodyId) {
  if (!initialized) init();
  if (!bodyId) {
    console.warn('[matryoshka] open() called with no bodyId');
    return;
  }
  const id = bodyId.toLowerCase();
  const tree = getBody(id);
  if (!tree) {
    console.warn(`[matryoshka] no body tree for "${id}"`);
    return;
  }
  if (!rootEl) {
    console.warn('[matryoshka] open() called before init() succeeded — no rootEl');
    return;
  }
  console.info(`[matryoshka] opening ${id}, tree has ${tree.children?.length || 0} children`);
  clearPendingPanels();
  currentPath = [id];
  isOpenState = true;
  rootEl.classList.add('visible');
  // On desktop, open fully. On mobile (bottom sheet) open MINIMISED — a
  // thin peek bar at the bottom showing just the body name so the canvas
  // stays visible. User taps the peek bar (or the expand chevron) to see
  // the full card. Avoids the "every tap covers half the screen" problem.
  const isMobile = window.matchMedia(COMPACT_LAYOUT_QUERY).matches;
  if (isMobile) {
    rootEl.classList.add('minimized');
  } else {
    rootEl.classList.remove('minimized');
  }
  render('forward');
  syncHash();
}

export function close() {
  isOpenState = false;
  currentPath = [];
  if (rootEl) rootEl.classList.remove('visible');
  clearPendingPanels();
  if (breadcrumbEl) breadcrumbEl.innerHTML = '';
  syncHash();
}

export function pushChild(childId) {
  const resolved = resolvePath(currentPath);
  if (!resolved) return;
  const exists = (resolved.node.children || []).find(c => c.id === childId);
  if (!exists) return;
  currentPath.push(childId);
  render('forward');
  syncHash();
}

export function popTo(depth) {
  if (depth < 1) depth = 1;
  if (depth >= currentPath.length) return;
  currentPath = currentPath.slice(0, depth);
  render('backward');
  syncHash();
}

function render(direction) {
  const resolved = resolvePath(currentPath);
  if (!resolved) {
    console.warn('[matryoshka] render() resolved no path for', currentPath);
    return;
  }
  const { node, trail } = resolved;
  console.info(`[matryoshka] rendering "${node.label}" (depth ${trail.length}, ${node.children?.length || 0} children, ${node.payload?.length || 0} payload sections)`);

  // Breadcrumb — full path, last is non-clickable
  breadcrumbEl.innerHTML = '';
  trail.forEach((n, idx) => {
    if (idx > 0) {
      const sep = document.createElement('span');
      sep.className = 'matryoshka-crumb-sep';
      sep.textContent = '›';
      breadcrumbEl.appendChild(sep);
    }
    const isLast = idx === trail.length - 1;
    const crumb = document.createElement('button');
    crumb.className = 'matryoshka-crumb' + (isLast ? ' is-active' : '');
    crumb.textContent = n.label;
    if (!isLast) {
      crumb.addEventListener('click', () => popTo(idx + 1));
    } else {
      crumb.disabled = true;
    }
    breadcrumbEl.appendChild(crumb);
  });

  // Panel: animate old out, new in
  const existingPanels = Array.from(stageEl.children);
  const oldPanel = existingPanels[existingPanels.length - 1] || null;
  existingPanels.slice(0, -1).forEach((panel) => panel.remove());
  const newPanel = renderPanel(node);
  newPanel.classList.add(direction === 'forward' ? 'enter-from-right' : 'enter-from-left');
  stageEl.appendChild(newPanel);

  // Force reflow before removing the enter class so the transition fires
  void newPanel.offsetHeight;
  newPanel.classList.remove('enter-from-right', 'enter-from-left');
  newPanel.classList.add('settled');

  if (oldPanel) {
    oldPanel.classList.add(direction === 'forward' ? 'exit-to-left' : 'exit-to-right');
    if (panelCleanupTimer) clearTimeout(panelCleanupTimer);
    panelCleanupTimer = setTimeout(() => {
      oldPanel.remove();
      panelCleanupTimer = null;
    }, 320);
  }
}

function clearPendingPanels() {
  if (panelCleanupTimer) {
    clearTimeout(panelCleanupTimer);
    panelCleanupTimer = null;
  }
  if (stageEl) stageEl.innerHTML = '';
}

function renderPanel(node) {
  const panel = document.createElement('div');
  panel.className = 'matryoshka-panel';
  panel.dataset.nodeId = node.id;

  const header = document.createElement('div');
  header.className = 'panel-header';

  const title = document.createElement('h2');
  title.className = 'panel-title';
  title.textContent = node.label;
  header.appendChild(title);

  if (node.type) {
    const type = document.createElement('p');
    type.className = 'panel-type';
    type.textContent = node.type;
    header.appendChild(type);
  } else if (node.summary && !node.payload) {
    const sum = document.createElement('p');
    sum.className = 'panel-type';
    sum.textContent = node.summary;
    header.appendChild(sum);
  }
  panel.appendChild(header);

  if (node.payload && node.payload.length) {
    node.payload.forEach(p => {
      const sec = renderPayloadSection(p);
      if (sec) panel.appendChild(sec);
    });
  }

  if (node.children && node.children.length) {
    const heading = document.createElement('h3');
    heading.className = 'panel-children-heading';
    heading.textContent = 'Drill deeper';
    panel.appendChild(heading);

    const list = document.createElement('ul');
    list.className = 'panel-children';
    node.children.forEach(child => {
      const li = document.createElement('li');
      li.className = 'panel-child';
      li.dataset.childId = child.id;
      const text = document.createElement('div');
      text.className = 'panel-child-text';
      const label = document.createElement('span');
      label.className = 'panel-child-label';
      label.textContent = child.label;
      text.appendChild(label);
      if (child.summary) {
        const summary = document.createElement('span');
        summary.className = 'panel-child-summary';
        summary.textContent = child.summary;
        text.appendChild(summary);
      }
      const arrow = document.createElement('span');
      arrow.className = 'panel-child-arrow';
      arrow.textContent = '→';
      li.appendChild(text);
      li.appendChild(arrow);
      li.addEventListener('click', () => pushChild(child.id));
      list.appendChild(li);
    });
    panel.appendChild(list);
  }

  return panel;
}

function renderPayloadSection(p) {
  const section = document.createElement('div');
  section.className = `panel-payload payload-${p.type}`;

  switch (p.type) {
    case 'description': {
      const para = document.createElement('p');
      para.className = 'payload-text';
      para.textContent = p.text;
      section.appendChild(para);
      return section;
    }

    case 'stats': {
      if (p.label) {
        const lbl = document.createElement('h3');
        lbl.className = 'payload-label';
        lbl.textContent = p.label;
        section.appendChild(lbl);
      }
      const dl = document.createElement('dl');
      dl.className = 'payload-stats';
      p.entries.forEach(e => {
        const row = document.createElement('div');
        row.className = 'stat-row';
        const dt = document.createElement('dt');
        dt.textContent = e.label;
        const dd = document.createElement('dd');
        if (e.readout) {
          dd.dataset.readout = e.readout;
          dd.dataset.body = e.body;
          dd.textContent = liveValue(e);
        } else {
          dd.textContent = e.value;
        }
        row.appendChild(dt);
        row.appendChild(dd);
        dl.appendChild(row);
      });
      section.appendChild(dl);
      return section;
    }

    case 'composition-bars': {
      const wrap = document.createElement('div');
      wrap.className = 'composition-bars';
      const max = Math.max(...p.entries.map(e => e.percent));
      p.entries.forEach(e => {
        const row = document.createElement('div');
        row.className = 'composition-row';
        const widthPct = (e.percent / max) * 100;
        const lbl = document.createElement('div');
        lbl.className = 'composition-label';
        const name = document.createElement('span');
        name.textContent = e.name;
        const pct = document.createElement('span');
        pct.className = 'composition-pct';
        pct.textContent = `${e.percent}%`;
        lbl.appendChild(name);
        lbl.appendChild(pct);
        const track = document.createElement('div');
        track.className = 'composition-bar-track';
        const fill = document.createElement('div');
        fill.className = 'composition-bar-fill';
        fill.style.width = `${widthPct}%`;
        fill.style.background = e.color || '#7dd3a8';
        track.appendChild(fill);
        row.appendChild(lbl);
        row.appendChild(track);
        if (e.note) {
          const note = document.createElement('div');
          note.className = 'composition-note';
          note.textContent = e.note;
          row.appendChild(note);
        }
        wrap.appendChild(row);
      });
      section.appendChild(wrap);
      return section;
    }

    case 'list': {
      const ul = document.createElement('ul');
      ul.className = 'payload-list';
      p.entries.forEach(e => {
        const li = document.createElement('li');
        const primary = document.createElement('div');
        primary.className = 'list-primary';
        primary.textContent = e.primary;
        li.appendChild(primary);
        if (e.secondary) {
          const sec = document.createElement('div');
          sec.className = 'list-secondary';
          sec.textContent = e.secondary;
          li.appendChild(sec);
        }
        ul.appendChild(li);
      });
      section.appendChild(ul);
      return section;
    }

    case 'links': {
      const ul = document.createElement('ul');
      ul.className = 'payload-links';
      p.entries.forEach(e => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = e.url;
        a.target = '_blank';
        a.rel = 'noopener';
        const span = document.createElement('span');
        span.textContent = e.label;
        const arrow = document.createElement('span');
        arrow.className = 'link-arrow';
        arrow.textContent = '↗';
        a.appendChild(span);
        a.appendChild(arrow);
        li.appendChild(a);
        ul.appendChild(li);
      });
      section.appendChild(ul);
      return section;
    }

    case 'callout': {
      const div = document.createElement('div');
      div.className = `payload-callout tone-${p.tone || 'info'}`;
      div.textContent = p.text;
      section.appendChild(div);
      return section;
    }

    case 'action': {
      // A call-to-action button. Fires a window 'matryoshka-action'
      // CustomEvent with the block's `action` id; main.js listens and
      // dispatches the real behaviour (e.g. the wormhole flythrough),
      // so the data layer stays free of app wiring.
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `payload-action-btn${p.tone ? ` tone-${p.tone}` : ''}`;
      btn.textContent = p.label || 'Go';
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        window.dispatchEvent(new CustomEvent('matryoshka-action', {
          detail: { action: p.action, label: p.label }
        }));
      });
      section.appendChild(btn);
      return section;
    }

    default:
      console.warn(`[matryoshka] unknown payload type: ${p.type}`);
      return null;
  }
}

function syncHash() {
  if (suppressHashSync) return;
  // Only WRITE when the matryoshka panel is meaningfully open. Closing
  // the panel must not clobber the focus hash that focusOnObject just
  // set on the URL — otherwise selecting any body without a hand-
  // authored matryoshka tree (ISS, satellites, comets, NEOs, probes,
  // belt bodies) immediately strips its own URL, since focusOnObject
  // falls through to matryoshka.close() when getBody() returns null.
  // The focus hash is owned by focusOnObject; matryoshka only extends
  // it with deeper segments when the user drills into the panel.
  if (!isOpenState || !currentPath.length) return;
  const newHash = `#/${currentPath.join('/')}`;
  if (window.location.hash !== newHash) {
    history.replaceState(null, '', newHash);
  }
}

function applyHash() {
  if (suppressHashSync) return;
  const raw = (window.location.hash || '').replace(/^#\/?/, '');
  if (!raw) {
    if (isOpenState) close();
    return;
  }
  // Decoded first: ids with a space or apostrophe ('voyager 1', "halley's
  // comet") arrive percent-encoded in a reloaded or shared link.
  const segments = raw.split('/').filter(Boolean).map((s) => {
    try { return decodeURIComponent(s).toLowerCase(); } catch { return s.toLowerCase(); }
  });
  if (!segments.length) return;
  const resolved = resolvePath(segments);
  if (!resolved) return;

  const direction = segments.length >= currentPath.length ? 'forward' : 'backward';
  currentPath = segments.slice(0, resolved.trail.length);
  isOpenState = true;
  rootEl.classList.add('visible');
  render(direction);
}

export const matryoshka = {
  init,
  open,
  close,
  pushChild,
  popTo,
  applyHash,
  setClock,
  isOpen: () => isOpenState,
  getCurrentPath: () => [...currentPath]
};
